import { useFormatPriceToStr } from '~~/common/utils/priceNuxt';
import { EMPTY_PRICE } from '~~/common/utils/price';
import { PT_PRICE_BUNDLE, PT_PRICE_COMBI, PT_PRICE_LOYALTY, PT_PRICE_STICKER } from '~~/common/config/priceTypes';
import type { determinePriceType } from '~~/common/utils/productPriceType';
import type { ProductVariantFe } from '~~/common/stores/product';

type Variant = ProductVariantFe | null | undefined;
type ProductPriceType = ReturnType<typeof determinePriceType>;
type PriceFormatter = ReturnType<typeof useFormatPriceToStr>;

type PriceSource = [source: Record<string, unknown> | undefined, modelKey: string, amountKey: string];

interface ProductPriceGroup {
  main: string;
  unit: string;
  unitkg: string;
  units: string[];
}

interface GetProductPricesArg {
  variant?: Variant;
  productPriceType?: ProductPriceType | null;
  priceFormatter?: PriceFormatter;
  isOneUnit?: boolean;
}

export function useProductPricesByVariant () {
  const formatPriceToStr = useFormatPriceToStr();
  return function ({
    variant,
    productPriceType,
    isOneUnit,
  }: Omit<GetProductPricesArg, 'priceFormatter'>) {
    return getProductPrices({
      variant,
      productPriceType,
      priceFormatter: formatPriceToStr,
      isOneUnit,
    });
  };
}

function getProductPrices ({
  variant,
  productPriceType,
  priceFormatter,
  isOneUnit = false,
}: GetProductPricesArg) {
  const variantObj: NonNullable<Variant> = (typeof variant === 'object' && variant) ? variant : {};
  const formatPrice: PriceFormatter = typeof priceFormatter === 'function' ? priceFormatter : () => '';
  const sources: Record<string, Record<string, PriceSource>> = {};
  const variantPInfo: Record<string, unknown> = variantObj.packageInfo || {};
  const toPrice = (priceValue: number) => {
    const currency = variantObj.price?.currency;
    // same placeholder formatPriceToStr returns for a non-string currency
    if (typeof currency !== 'string') {
      return EMPTY_PRICE;
    }
    return formatPrice({
      priceValue,
      currency,
    });
  };
  const priceType = productPriceType ? productPriceType.priceType : null;
  const isApplicable = productPriceType ? productPriceType.isApplicable : false;
  const primaryType = isApplicable && priceType;
  const secondaryType = !isApplicable && priceType;
  if (primaryType === PT_PRICE_STICKER) {
    sources.primary = {
      main: [variantObj, 'stickerPrice', 'grossDiscounted'],
      unit: [variantPInfo, 'stickerUnitPrice', 'grossDiscounted'],
      unitkg: [variantPInfo, 'stickerPricePerKg', 'grossDiscounted'],
    };
  } else if (primaryType === PT_PRICE_LOYALTY) {
    sources.primary = {
      main: [variantObj, 'loyaltyPrice', 'grossDiscounted'],
      unit: [variantPInfo, 'loyaltyUnitPrice', 'grossDiscounted'],
      unitkg: [variantPInfo, 'loyaltyPricePerKg', 'grossDiscounted'],
    };
  } else if (primaryType === PT_PRICE_COMBI) {
    sources.primary = {
      main: [variantObj, 'combiPrice', 'grossDiscounted'],
      unit: [variantPInfo, 'combiUnitPrice', 'grossDiscounted'],
      unitkg: [variantPInfo, 'combiPricePerKg', 'grossDiscounted'],
    };
  } else if (primaryType === PT_PRICE_BUNDLE) {
    sources.primary = {
      main: [variantObj, 'bundlePrice', 'grossDiscounted'],
      unit: [variantPInfo, 'bundleUnitPrice', 'grossDiscounted'],
      unitkg: [variantPInfo, 'bundlePricePerKg', 'grossDiscounted'],
    };
  } else {
    sources.primary = {
      main: [variantObj, 'price', 'grossDiscounted'],
      unit: [variantPInfo, 'unitPrice', 'grossDiscounted'],
      unitkg: [variantPInfo, 'pricePerKg', 'grossDiscounted'],
    };
  }
  if (secondaryType === PT_PRICE_STICKER) {
    sources.secondary = {
      main: [variantObj, 'stickerPrice', 'grossDiscounted'],
      unit: [variantPInfo, 'stickerUnitPrice', 'grossDiscounted'],
      unitkg: [variantPInfo, 'stickerPricePerKg', 'grossDiscounted'],
    };
  } else if (secondaryType === PT_PRICE_LOYALTY) {
    sources.secondary = {
      main: [variantObj, 'loyaltyPrice', 'grossDiscounted'],
      unit: [variantPInfo, 'loyaltyUnitPrice', 'grossDiscounted'],
      unitkg: [variantPInfo, 'loyaltyPricePerKg', 'grossDiscounted'],
    };
  } else if (secondaryType === PT_PRICE_COMBI) {
    sources.secondary = {
      main: [variantObj, 'combiPrice', 'grossDiscounted'],
      unit: [variantPInfo, 'combiUnitPrice', 'grossDiscounted'],
      unitkg: [variantPInfo, 'combiPricePerKg', 'grossDiscounted'],
    };
  } else if (secondaryType === PT_PRICE_BUNDLE) {
    sources.secondary = {
      main: [variantObj, 'bundlePrice', 'grossDiscounted'],
      unit: [variantPInfo, 'bundleUnitPrice', 'grossDiscounted'],
      unitkg: [variantPInfo, 'bundlePricePerKg', 'grossDiscounted'],
    };
  } else {
    sources.secondary = {
      main: [variantObj, 'price', 'gross'],
      unit: [variantPInfo, 'unitPrice', 'gross'],
      unitkg: [variantPInfo, 'pricePerKg', 'gross'],
    };
  }
  const isLoose = !!variantObj.loose?.loose;
  return {
    primary: resolveGroup(sources.primary),
    secondary: resolveGroup(sources.secondary),
  };

  function resolveGroup (source: Record<string, PriceSource> | undefined): ProductPriceGroup {
    const price: Record<string, string> = {};
    for (const key in source) {
      const [priceParent, priceObjKey, priceKey] = ((isOneUnit && isLoose && key === 'main') ? source.unit : source[key]) ?? [];
      const priceModel = priceObjKey === undefined ? undefined : priceParent?.[priceObjKey];
      const amount = (isRecord(priceModel) && priceKey !== undefined) ? priceModel[priceKey] : undefined;
      if (typeof amount === 'number') {
        price[key] = toPrice(amount);
        if (key === 'unit') {
          const packageUnit = typeof priceParent?.packageUnit === 'string' ? priceParent.packageUnit.toLowerCase() : undefined;
          price[key] += `/${packageUnit === 'liter' ? 'l' : packageUnit}`;
        } else if (key === 'unitkg') {
          price[key] += '/kg';
        }
      } else {
        price[key] = '';
      }
    }
    const unit = price.unit ?? '';
    const unitkg = price.unitkg ?? '';
    return {
      main: price.main ?? '',
      unit,
      unitkg,
      units: [unit, unitkg].filter(e => e),
    };
  }
}

export function useRollPriceByVariant () {
  const priceFormatter = useFormatPriceToStr();
  return {
    getRollPriceByVariant ({ variant }: { variant: Variant }) {
      return getRollPrice({
        variant,
        priceFormatter,
      });
    },
  };
}

function getRollPrice ({
  variant,
  priceFormatter,
}: {
  variant: Variant;
  priceFormatter: PriceFormatter;
}) {
  const currency = variant?.roll?.price?.currency;
  if (variant?.roll?.price?.grossDiscounted) {
    if (typeof currency !== 'string') {
      return EMPTY_PRICE;
    }
    return priceFormatter({
      priceValue: variant.roll.price.grossDiscounted,
      currency,
    });
  } else {
    return '';
  }
}

function isRecord (value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
